/**
 * Circuit Breaker Pattern Implementation
 * Implementation: Step 10 - Prevent retry storms with 5xx filtering
 *
 * Prevents retry storms during backend outages by failing fast after
 * a threshold of consecutive 5xx errors, then auto-retrying after a timeout.
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Too many failures, fast-fail all requests
 * - HALF_OPEN: Testing if service recovered after timeout
 *
 * Transitions:
 * - CLOSED --[threshold failures]--> OPEN
 * - OPEN --[timeout expires]--> HALF_OPEN
 * - HALF_OPEN --[success]--> CLOSED
 * - HALF_OPEN --[failure]--> OPEN
 */

import debug from 'debug';
import { DD_AUTH_EVENTS, logAuthEvent } from './datadogEvents';
import { CIRCUIT_BREAKER_TIMEOUT_MS, CIRCUIT_BREAKER_THRESHOLD } from '../config/auth.config';

const debugCircuit = debug('auth:circuit');
const debugStep10 = debug('impl:step10:circuit');
const debugError = debug('auth:circuit:error');

export class CircuitBreakerError extends Error {
  constructor(message, state) {
    super(message);
    this.name = 'CircuitBreakerError';
    this.code = 'CIRCUIT_OPEN';
    this.state = state;
  }
}

export class CircuitBreaker {
  /**
   * @param {Object} options
   * @param {number} options.threshold - Number of failures before opening (default: 5)
   * @param {number} options.timeout - Milliseconds to wait before half-open (default: 60000)
   * @param {string} options.name - Name for logging/debugging (default: 'CircuitBreaker')
   */
  constructor(options = {}) {
  this.threshold = options.threshold || CIRCUIT_BREAKER_THRESHOLD;
  this.timeout = options.timeout || CIRCUIT_BREAKER_TIMEOUT_MS; // 60 seconds
    this.name = options.name || 'CircuitBreaker';

    this.state = 'CLOSED';
    this.failures = 0;
    this.nextAttempt = null;
    
    debugStep10('step 10: circuit breaker initialized', { 
      name: this.name,
      threshold: this.threshold,
      timeout: this.timeout 
    });
    debugCircuit('[%s] Circuit breaker initialized (threshold: %d, timeout: %dms)', 
      this.name, this.threshold, this.timeout);
  }

  /**
   * Execute a function with circuit breaker protection
   *
   * @param {Function} fn - Async function to execute
   * @returns {Promise} Result of fn() or rejection if circuit is open
   */
  async execute(fn) {
    debugStep10('step 10: circuit execute', { 
      name: this.name,
      state: this.state,
      failures: this.failures 
    });
    debugCircuit('[%s] Executing with state: %s, failures: %d', this.name, this.state, this.failures);
    
    // Check if circuit is open
    if (this.state === 'OPEN') {
      const now = Date.now();

      // Check if timeout expired - transition to HALF_OPEN
      if (this.nextAttempt && now >= this.nextAttempt) {
        debugStep10('step 10: timeout expired - transitioning OPEN → HALF_OPEN', { 
          name: this.name,
          timeoutMs: this.timeout 
        });
        debugCircuit('[%s] Timeout expired, transitioning to HALF_OPEN', this.name);
        console.debug(`[CircuitBreaker:${this.name}] Timeout expired, transitioning to HALF_OPEN`);
        this.state = 'HALF_OPEN';
      } else {
        // Still open, fast-fail
        const waitTime = this.nextAttempt ? this.nextAttempt - now : null;
        debugStep10('step 10: circuit OPEN - fast-failing request', { 
          name: this.name,
          waitTimeMs: waitTime,
          failures: this.failures 
        });
        debugError('[%s] Circuit is OPEN, rejecting request (wait: %dms)', this.name, waitTime);
        
        const error = new CircuitBreakerError(
          `Circuit breaker is OPEN for ${this.name}`,
          this.state
        );

        // Track in Datadog
        logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_REJECTED, {
          name: this.name,
          state: this.state,
          failures: this.failures,
          nextAttemptIn: waitTime,
        });

        throw error;
      }
    }

    // Execute the function
    try {
      debugStep10('step 10: executing function through circuit breaker');
      const result = await fn();

      // Success - reset failures and close circuit
      debugStep10('step 10: execution successful', { 
        name: this.name,
        previousState: this.state 
      });
      debugCircuit('[%s] Execution successful', this.name);
      this.onSuccess();

      return result;
    } catch (error) {
      // Check if this is a 5xx server error
      const isServerError = this.isServerError(error);
      const status = error?.response?.status;

      debugStep10('step 10: execution failed - checking if 5xx', {
        name: this.name,
        status,
        isServerError,
        errorCode: error?.code
      });

      if (isServerError) {
        debugStep10('step 10: 5xx error detected - counting against circuit', {
          name: this.name,
          status,
          currentFailures: this.failures
        });
        debugError('[%s] Server error detected (status: %d)', this.name, status);
        this.onFailure(error);
      } else {
        debugStep10('step 10: non-5xx error - not counting against circuit', {
          name: this.name,
          status: status || 'N/A',
          errorCode: error?.code
        });
        debugCircuit('[%s] Non-server error (status: %s), not counting against circuit',
          this.name, status || 'N/A');
      }

      // Re-throw error regardless
      throw error;
    }
  }

  /**
   * Check if error is a server error (5xx status code)
   *
   * @param {Error} error
   * @returns {boolean}
   */
  isServerError(error) {
    const status = error?.response?.status;
    return status >= 500 && status < 600;
  }

  /**
   * Handle successful execution
   */
  onSuccess() {
    const wasOpen = this.state !== 'CLOSED';
    const previousState = this.state;
    const previousFailures = this.failures;

    // Reset state
    this.failures = 0;
    this.state = 'CLOSED';
    this.nextAttempt = null;

    if (wasOpen) {
      debugStep10('step 10: circuit closed after success', { 
        name: this.name,
        previousState,
        previousFailures,
        transition: `${previousState} → CLOSED` 
      });
      debugCircuit('[%s] Circuit closed after successful request (was: %s, failures: %d)', 
        this.name, previousState, previousFailures);
      console.info(`[CircuitBreaker:${this.name}] Circuit closed after successful request`);

      // Track recovery in Datadog
      logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_CLOSED, {
        name: this.name,
      });
    } else {
      debugCircuit('[%s] Execution succeeded, circuit remains closed', this.name);
    }
  }

  /**
   * Handle failed execution (5xx error)
   *
   * @param {Error} error
   */
  onFailure(error) {
    const errorStatus = error?.response?.status;
    
    // If coming from HALF_OPEN, reset failure count to 1
    if (this.state === 'HALF_OPEN') {
      this.failures = 1;
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.timeout;

      debugStep10('step 10: HALF_OPEN test failed - reopening circuit', { 
        name: this.name,
        errorStatus,
        timeoutMs: this.timeout,
        transition: 'HALF_OPEN → OPEN' 
      });
      debugError('[%s] HALF_OPEN test failed (status: %d), returning to OPEN for %dms', 
        this.name, errorStatus, this.timeout);
      console.error(
        `[CircuitBreaker:${this.name}] HALF_OPEN test failed, returning to OPEN. ` +
        `Will retry in ${this.timeout}ms`
      );

      // Track in Datadog
      logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_OPENED, {
        name: this.name,
        failures: this.failures,
        threshold: this.threshold,
        timeoutMs: this.timeout,
        lastErrorStatus: errorStatus,
        fromHalfOpen: true,
      });

      return;
    }

    this.failures++;

    debugStep10('step 10: 5xx failure counted', { 
      name: this.name,
      failures: this.failures,
      threshold: this.threshold,
      errorStatus 
    });
    debugCircuit('[%s] Failure %d/%d (status: %d)', 
      this.name, this.failures, this.threshold, errorStatus);
    console.debug(
      `[CircuitBreaker:${this.name}] Failure ${this.failures}/${this.threshold} - Status: ${errorStatus}`
    );

    // Check if threshold exceeded
    if (this.failures >= this.threshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.timeout;

      debugStep10('step 10: threshold exceeded - opening circuit', { 
        name: this.name,
        failures: this.failures,
        threshold: this.threshold,
        timeoutMs: this.timeout,
        errorStatus,
        transition: 'CLOSED → OPEN' 
      });
      debugError('[%s] Threshold exceeded, opening circuit for %dms', this.name, this.timeout);
      console.error(
        `[CircuitBreaker:${this.name}] Circuit opened after ${this.failures} failures. ` +
        `Will retry in ${this.timeout}ms`
      );

      // Track in Datadog
      logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_OPENED, {
        name: this.name,
        failures: this.failures,
        threshold: this.threshold,
        timeoutMs: this.timeout,
        lastErrorStatus: errorStatus,
      });
    }
  }

  /**
   * Get current circuit breaker state
   *
   * @returns {string} Current state: 'CLOSED', 'OPEN', or 'HALF_OPEN'
   */
  getState() {
    return this.state;
  }

  /**
   * Get current failure count
   *
   * @returns {number}
   */
  getFailures() {
    return this.failures;
  }

  /**
   * Manually reset the circuit breaker (for testing/admin)
   */
  reset() {
    debugCircuit('[%s] Manually reset (was: %s, failures: %d)', 
      this.name, this.state, this.failures);
    console.info(`[CircuitBreaker:${this.name}] Manually reset`);
    this.failures = 0;
    this.state = 'CLOSED';
    this.nextAttempt = null;
  }
}
